import React from 'react';

const H2 = ({ children }) => (
  <h2 className="font-serif text-cream-50 text-2xl md:text-3xl mb-4">{children}</h2>
);

const Box = ({ children, testId }) => (
  <div data-testid={testId} className="mt-4 bg-forest-950/50 border border-forest-800 p-5 text-cream-100/85 space-y-1">
    {children}
  </div>
);

const Ext = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="text-sage-400 hover:underline break-words">
    {children}
  </a>
);

const Mail = ({ to }) => (
  <a href={`mailto:${to}`} className="text-sage-400 hover:underline">{to}</a>
);

const Tel = ({ num, label }) => (
  <a href={`tel:${num}`} className="text-sage-400 hover:underline">{label}</a>
);

function FirmContact() {
  return (
    <Box testId="complaints-firm-contact">
      <p className="text-cream-50 font-serif text-xl mb-2">Perrin Myddelton a trading name of Hedges Law</p>
      <p>16 Market Place, Wallingford, Oxon, OX10 0AE</p>
      <p><strong className="text-cream-50">Tel:</strong> <Tel num="01491839839" label="01491 839839" /></p>
      <p><strong className="text-cream-50">Email:</strong> <Mail to="hello@hedgeslaw.co.uk" /></p>
    </Box>
  );
}

function HowToComplain() {
  return (
    <div>
      <H2>How to make a complaint</H2>
      <ol className="list-decimal pl-6 space-y-4">
        <li>
          You may contact the person working on your case to discuss your concerns, or their supervisor. If they are
          unable to resolve your complaint to your satisfaction, you may complain directly to our Complaints Handling
          Officer (CHO), <strong className="text-cream-50">Nicola Poole</strong>, who has overall responsibility for
          complaints. Alternatively, you may complain directly to the CHO if you wish to do so; you do not have to
          complain to the person involved first. You can complain by telephone, in person, by email or by post, using
          our usual contact details. You do not have to use the words “formal complaint”. Our contact details are:
          <FirmContact />
        </li>
        <li>
          To help us respond, please provide the following details where possible:
          <ul className="list-disc pl-6 mt-2 space-y-1 text-cream-100/80">
            <li>Your full name and contact details</li>
            <li>Your file reference number if you know it</li>
            <li>What you think we have got wrong</li>
            <li>How you think we could best resolve your concerns</li>
          </ul>
        </li>
      </ol>
      <p className="mt-4">
        We recognise a complaint as an oral or written expression of dissatisfaction alleging that you have suffered,
        or may suffer, financial loss, distress, inconvenience or other detriment. Missing details will not prevent us
        from considering your complaint.
      </p>
      <p className="mt-4">
        If you need help to complain or a reasonable adjustment, please tell us. We can discuss accessible formats,
        communication needs or working with someone authorised to assist you.
      </p>
    </div>
  );
}

function WhatToExpect() {
  return (
    <>
      <div>
        <H2>What you can expect from us</H2>
        <p>
          We will acknowledge your complaint in writing within five working days of receiving it. We will provide this
          procedure, confirm who will handle your complaint, and set out the next steps and expected timeline.
        </p>
        <p className="mt-4">
          We will deal with your complaint fairly, promptly and free of charge. We will agree how to communicate with
          you and provide regular progress updates, including any reasons for delay and revised response dates.
        </p>
      </div>
      <div>
        <H2>Early resolution</H2>
        <p>
          Where appropriate, we will discuss your concerns and the outcome you would like, aiming to resolve the
          complaint quickly through an explanation, apology or other action. We will normally aim to complete this
          stage within ten working days of our acknowledgment of your complaint. We will confirm any resolution agreed
          with you and the action we will take in writing. If early resolution is unsuccessful or unsuitable, we will
          move to a full investigation and tell you.
        </p>
      </div>
    </>
  );
}

function FullInvestigation() {
  return (
    <div>
      <H2>Full investigation</H2>
      <p>
        We will review your file and the relevant evidence, speak to those involved and give you an opportunity to
        explain your concerns. Where possible, someone not directly involved in the complaint will investigate. The
        maximum time we will take to provide you with a full and final response will be eight weeks, but where
        possible we will respond sooner. Our response will address your concerns, explain our findings and any remedy,
        and confirm that our internal procedure is complete.
      </p>
      <p className="mt-4">
        The eight-week period runs from when you first make the complaint; moving between stages does not restart it.
        We will provide our final response within that period. If your complaint remains unresolved to your
        satisfaction after eight weeks, we will write to explain your right to approach the Legal Ombudsman, its
        contact details and the applicable time limits. You do not have to wait longer for our investigation to finish.
      </p>
      <p className="mt-4">
        We will include the Legal Ombudsman referral information and time limits in our final written response,
        including where a complaint is resolved through early resolution. We will record complaints and outcomes and
        use them to improve our service.
      </p>
      <p className="mt-4">
        This procedure appears on our website and is also available on request. We will provide written complaints
        information at engagement, at the end of your matter, on request and when you complain during a matter.
      </p>
      <p className="mt-4">
        Our approach draws on the Legal Ombudsman’s voluntary Model Complaints Resolution Procedure.
      </p>
    </div>
  );
}

function NotSatisfied() {
  return (
    <div>
      <H2>If you are not satisfied with the outcome</H2>
      <p>
        The Legal Ombudsman can independently consider complaints about legal services and charges where the complaint
        falls within its jurisdiction. Its service is free. Eligible complainants include individuals and certain small
        businesses, charities, clubs and trusts. The Ombudsman’s website explains eligibility; it decides whether it
        can consider your complaint.
      </p>
      <p className="mt-4">
        You should normally give us an opportunity to resolve your complaint first. You can approach the Legal
        Ombudsman if you are dissatisfied with our final written response, or if eight weeks have passed since you
        complained without satisfactory resolution. In exceptional circumstances it may consider a complaint sooner.
      </p>
      <p className="mt-4">
        You must normally refer your complaint to the Legal Ombudsman within one year of the act or omission
        complained about, or within one year of when you should reasonably have known there was cause for complaint.
        You must also normally refer it within six months of the date of our final written response. The six-month
        limit applies where that response prominently explains your right to complain to the Ombudsman, provides its
        full contact details and warns of the six-month deadline. The Ombudsman may extend the time limits where it
        considers this fair and reasonable.
      </p>
    </div>
  );
}

function NonClient() {
  return (
    <div>
      <H2>Non-client complaints</H2>
      <p>
        You may still be able to complain to us if you are not a client but are affected by our service, for example
        if you are the beneficiary of a trust or estate administered by us. You may access our complaints process by
        writing to the CHO as described above. We will apply the complaints procedure in the same way.
      </p>
      <p className="mt-4">Further information can be obtained direct from the Legal Ombudsman:</p>
      <Box testId="complaints-ombudsman-contact">
        <p className="text-cream-50 font-serif text-xl mb-2">Legal Ombudsman</p>
        <p>PO Box 6167, Slough, SL1 0EH</p>
        <p><strong className="text-cream-50">Visit:</strong> <Ext href="https://www.legalombudsman.org.uk">www.legalombudsman.org.uk</Ext></p>
        <p><strong className="text-cream-50">Call:</strong> <Tel num="03005550333" label="0300 555 0333" /></p>
        <p><strong className="text-cream-50">Email:</strong> <Mail to="enquiries@legalombudsman.org.uk" /></p>
      </Box>
      <p className="mt-4">
        If our procedure is exhausted and we cannot settle your complaint, we will also tell you in writing the name
        and website address of an approved alternative dispute resolution body competent to deal with it, and whether
        we agree to use its scheme. This does not affect any right to approach the Legal Ombudsman.
      </p>
    </div>
  );
}

function Regulators() {
  return (
    <div>
      <p>
        The Solicitors Regulation Authority deals with concerns about professional conduct or breaches of its rules,
        such as dishonesty, misuse of client money or unlawful discrimination. It does not normally resolve service or
        fee complaints or award compensation. You can report a conduct concern without waiting for our complaints
        procedure to finish. Its contact details are:
      </p>
      <Box testId="complaints-sra-contact">
        <p className="text-cream-50 font-serif text-xl mb-2">Solicitors Regulation Authority</p>
        <p>The Cube, 199 Wharfside Street, Birmingham, B1 1RN</p>
        <p><strong className="text-cream-50">Visit:</strong> <Ext href="https://www.sra.org.uk/consumers/problems/report-solicitor/">https://www.sra.org.uk/consumers/problems/report-solicitor/</Ext></p>
        <p><strong className="text-cream-50">Telephone:</strong> <Tel num="03706062555" label="0370 606 2555" /></p>
      </Box>
      <p className="mt-6">
        The Information Commissioner’s Office can help with advice if you are concerned about data protection issues.
        Below are their contact details:
      </p>
      <Box testId="complaints-ico-contact">
        <p className="text-cream-50 font-serif text-xl mb-2">Information Commissioner’s Office</p>
        <p>Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF</p>
        <p><strong className="text-cream-50">Visit:</strong> <Ext href="https://ico.org.uk/global/contact-us/contact-us-public/">https://ico.org.uk/global/contact-us/contact-us-public/</Ext></p>
        <p><strong className="text-cream-50">Telephone:</strong> <Tel num="03031231113" label="0303 123 1113" /></p>
      </Box>
    </div>
  );
}

export default function Complaints() {
  return (
    <div className="pt-24" data-testid="complaints-page">
      <section className="bg-forest-950 py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <p className="text-sage-400 tracking-[0.3em] text-xs uppercase mb-5">Legal</p>
          <h1 className="font-serif text-cream-50 text-5xl md:text-6xl leading-tight">Complaints Procedure</h1>
          <p className="mt-6 text-cream-100/70">Perrin Myddelton a trading name of Hedges Law</p>
        </div>
      </section>

      <section className="bg-forest-900 py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-10 space-y-12 text-cream-100/85 leading-relaxed">
          <div>
            <H2>Our commitment</H2>
            <p>
              We want to give you the best possible service. If you are unhappy or concerned about our services or
              charges, please let us know so that we can try to put things right. You can complain during or after
              your matter. Raising a complaint will not adversely affect how we handle your case.
            </p>
          </div>
          <div>
            <H2>At the start of your case</H2>
            <p>
              At the start of your case you will be given the name and contact details of the person responsible for
              your case, along with the details of their supervisor.
            </p>
          </div>
          <HowToComplain />
          <WhatToExpect />
          <FullInvestigation />
          <NotSatisfied />
          <NonClient />
          <Regulators />
        </div>
      </section>
    </div>
  );
}
